import React from 'react';
import { css, useTheme } from '@emotion/react';
import {
  FormControlLabel,
  FormHelperText,
  Radio,
  RadioGroup,
  type FormControlLabelProps,
  type RadioGroupProps,
  type RadioProps,
} from '@mui/material';
import { type RestMultiRowSurveyCard } from '@bbkAdminUtils/api-client/rest-cards-types';
import { type ReactFCC } from '@bbkAdminUtils/utility-types';
import { type EmotionTheme } from '@bbkFunnel/pages/theme';
import { type SurveyMultiRowResponseHandler } from '@bbkFunnelComponents/survey-multi-row';

type Props = {
  response: Pick<RestMultiRowSurveyCard['provides'], 'sentiment'>;
  handler: SurveyMultiRowResponseHandler;
  error?: string;
  lowText: string;
  highText: string;
};
export const Sentiment: ReactFCC<Props> = (props) => {
  // console.log('[Sentiment]', props);
  const {
    lowText,
    highText,
    handler,
    response: { sentiment },
    error: sentimentError,
  } = props;

  const theme = useTheme() as EmotionTheme;

  const layout = css({
    display: 'flex',
    justifyContent: 'space-between',
  });
  const label = css({
    opacity: 0.5,
    fontSize: '0.77em',
    lineHeight: '22px',
  });

  const radioColor = sentimentError ? theme.errorVal : theme.textColor;
  const radioColorChecked = sentimentError
    ? theme.errorVal
    : theme.primaryColor;

  const WrappedLabel = (props: FormControlLabelProps) => (
    <FormControlLabel
      {...props}
      sx={{
        margin: 0,
        '& .MuiFormControlLabel-label': {
          color: radioColor,
          fontSize: '.8em',
          fontWeight: sentimentError ? 'normal' : 'lighter',
          fontFamily: theme.fontFamily,
        },
      }}
    />
  );

  const WrappedRadio = (radioProps: RadioProps) => (
    <Radio
      {...radioProps}
      color="default"
      sx={{
        margin: '0 -10px',
        color: radioColor,
        opacity: 0.4,
        '&.Mui-checked': {
          color: radioColorChecked,
          opacity: 1,
        },
      }}
    />
  );

  const items = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((r) => (
    <WrappedLabel
      control={<WrappedRadio />}
      labelPlacement="bottom"
      key={r}
      value={r}
      label={r}
    />
  ));

  const WrappedRadioGroup = (props: RadioGroupProps) => (
    <RadioGroup
      {...props}
      sx={{
        flexDirection: 'row',
      }}
    />
  );

  return (
    <div className="poll">
      <div css={[layout, label]}>
        <span>{lowText}</span>
        <span>{highText}</span>
      </div>
      <WrappedRadioGroup
        name="sentiment"
        value={sentiment}
        onChange={handler}
        className="bbk-sentiment-layout"
        css={[layout]}
      >
        {items}
      </WrappedRadioGroup>
      {sentimentError && (
        <FormHelperText style={{ fontSize: 12, color: theme.errorVal }}>
          {sentimentError}
        </FormHelperText>
      )}
    </div>
  );
};
